/**
 * Phone-width roster: the selected day as a read-only list, line by line.
 *
 * The desktop board is a drag-and-drop timeline that needs a mouse and about
 * 1180px. On a phone a supervisor mostly wants to know who is where today and
 * which crew lines are still open, so this view shows exactly that from the
 * same derived lanes the board draws. Planning moves stay on larger screens.
 */
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { hhmm } from "../../../utils/time";
import { groupCrewLines, LaneCard, LaneCrewGroup, LaneRow, LaneStep } from "../utils/lane-rows"; // eslint-disable-line sort-imports
import { eyebrowSx, fmtHours, lbb } from "../utils/tokens";

export interface PhoneRosterProps {
	dateLabel: string;
	locationName: string;
	rows: LaneRow[];
	utilPct: number;
	coveredPct: number;
	overtimeHours: number;
	peopleCount: number;
	freeCount: number;
	onPrevious: () => void;
	onNext: () => void;
	onToday: () => void;
}

const navButtonStyle: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	minWidth: 40,
	height: 40,
	padding: "0 10px",
	border: lbb.borderInk,
	borderRadius: 4,
	background: lbb.paper,
	color: lbb.navy,
	fontFamily: lbb.fontUi,
	fontSize: 14,
	fontWeight: 700,
	cursor: "pointer",
};

const chipStyle = (isOut: boolean): React.CSSProperties => ({
	display: "inline-flex",
	alignItems: "center",
	padding: "5px 9px",
	borderRadius: 4,
	background: isOut ? lbb.brickTint : lbb.blockFill,
	color: isOut ? lbb.brickDeep : lbb.navy,
	fontFamily: lbb.fontUi,
	fontSize: 13,
	fontWeight: 600,
	textDecoration: isOut ? "line-through" : "none",
});

const openChipStyle: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	padding: "4px 9px",
	borderRadius: 4,
	border: `1.5px dashed ${lbb.brick}`,
	color: lbb.brick,
	fontFamily: lbb.fontUi,
	fontSize: 13,
	fontWeight: 700,
};

const coverageColor = (have: number, needed: number): string => (have < needed ? lbb.brick : lbb.success);

/** One chip per person, even when their day is split into several blocks. */
const distinctPeople = (cards: LaneCard[]): LaneCard[] => {
	const seen = new Map<string, LaneCard>();
	cards.forEach((card) => {
		if (!seen.has(card.workerId)) seen.set(card.workerId, card);
	});
	return Array.from(seen.values());
};

const PhoneMetric = ({ label, value, color }: { label: string; value: string; color: string }) => (
	<div style={{ minWidth: 0 }}>
		<div style={{ ...eyebrowSx, fontSize: 10, letterSpacing: "0.08em", color: lbb.ink60, lineHeight: 1.3 }}>{label}</div>
		<div style={{ fontFamily: lbb.fontBrand, fontSize: 24, fontWeight: 700, color, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
	</div>
);

const CrewGroup = ({ group }: { group: LaneCrewGroup }) => {
	const { t } = useTranslation();
	const people = useMemo(() => distinctPeople(group.cards), [group.cards]);
	// Slots are counted per shift segment, so one missing person can be two
	// slots; the chip counts people still needed. A group that has its heads but
	// still leaves part of a segment uncovered reads as partly open.
	const shortBy = Math.max(0, group.needed - group.have);
	const isPartlyOpen = shortBy === 0 && group.slots.length > 0;
	const hasRequirement = group.needed > 0;
	return (
		<div style={{ padding: "10px 12px", borderTop: lbb.borderHair }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
				<div style={{ minWidth: 0, fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink }}>
					<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>{group.operationName}</span>
					<div style={{ fontWeight: 600, marginTop: 2 }}>{group.jobRole}</div>
				</div>
				{hasRequirement ? (
					<div style={{ fontFamily: lbb.fontMono, fontSize: 12, fontWeight: 700, color: coverageColor(group.have, group.needed), whiteSpace: "nowrap" }}>
						{t("roster-phone_have-of-needed", { have: group.have, needed: group.needed })}
					</div>
				) : null}
			</div>
			{people.length > 0 || shortBy > 0 || isPartlyOpen ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
					{people.map((card) => (
						<span key={card.workerId} style={chipStyle(card.isOut)}>
							{card.workerName}
							{card.isOut ? <span style={{ textDecoration: "none", marginLeft: 4 }}>{t("roster-phone_out")}</span> : null}
						</span>
					))}
					{shortBy > 0 ? <span style={openChipStyle}>{t("roster-phone_open-slots", { count: shortBy })}</span> : null}
					{isPartlyOpen ? <span style={openChipStyle}>{t("roster-phone_partly-open")}</span> : null}
				</div>
			) : null}
		</div>
	);
};

const StepCard = ({ step }: { step: LaneStep }) => {
	const { t } = useTranslation();
	const groups = useMemo(() => groupCrewLines(step.phases), [step.phases]);
	const meta = [step.subtitle, `${hhmm(step.start)} – ${hhmm(step.end)}`].filter(Boolean).join(" · ");
	return (
		<article style={{ border: lbb.borderHair, borderRadius: 6, background: lbb.paper, overflow: "hidden" }}>
			<header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: lbb.alloyWarm }}>
				<div style={{ minWidth: 0 }}>
					<div style={{ fontFamily: lbb.fontBrand, fontSize: 15, fontWeight: 700, color: lbb.navy }}>{step.title}</div>
					<div style={{ fontFamily: lbb.fontMono, fontSize: 11, color: lbb.ink60, marginTop: 3 }}>{meta}</div>
				</div>
				{step.crewNeeded > 0 ? (
					<div
						style={{ fontFamily: lbb.fontMono, fontSize: 15, fontWeight: 700, color: coverageColor(step.crewHave, step.crewNeeded), whiteSpace: "nowrap" }}
						aria-label={t("roster-phone_crew-filled", { have: step.crewHave, needed: step.crewNeeded })}
					>
						{step.crewHave}/{step.crewNeeded}
					</div>
				) : null}
			</header>
			{groups.map((group) => (
				<CrewGroup key={group.key} group={group} />
			))}
		</article>
	);
};

const PhoneRoster = ({
	dateLabel,
	locationName,
	rows,
	utilPct,
	coveredPct,
	overtimeHours,
	peopleCount,
	freeCount,
	onPrevious,
	onNext,
	onToday,
}: PhoneRosterProps) => {
	const { t } = useTranslation();
	const lines = useMemo(() => rows.filter((row) => row.steps.length > 0), [rows]);

	return (
		<div style={{ padding: "12px 0 24px", background: lbb.desk }}>
			<div style={{ background: lbb.paper, border: lbb.borderInk, borderRadius: 6, overflow: "hidden" }}>
				<div style={{ padding: "14px 12px 12px", borderBottom: lbb.borderInk }}>
					<div style={{ ...eyebrowSx, color: lbb.ink60 }}>
						{t("roster-phone_eyebrow")}
						{locationName ? ` · ${locationName}` : ""}
					</div>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 8 }}>
						<h2 style={{ margin: 0, fontFamily: lbb.fontBrand, fontSize: 22, color: lbb.navy, minWidth: 0 }}>{dateLabel}</h2>
						<div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
							<button type='button' style={navButtonStyle} onClick={onPrevious} aria-label={t("roster-phone_previous-day")}>
								<ChevronLeft fontSize='small' />
							</button>
							<button type='button' style={navButtonStyle} onClick={onToday}>
								{t("roster-phone_today")}
							</button>
							<button type='button' style={navButtonStyle} onClick={onNext} aria-label={t("roster-phone_next-day")}>
								<ChevronRight fontSize='small' />
							</button>
						</div>
					</div>
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
						gap: 10,
						padding: 12,
						background: lbb.alloyWarm,
						borderBottom: lbb.borderHair,
					}}
				>
					<PhoneMetric
						label={t("roster-phone_utilization")}
						value={`${utilPct}%`}
						color={utilPct > 95 ? lbb.brick : utilPct < 70 ? lbb.warning : lbb.navy}
					/>
					<PhoneMetric label={t("roster-phone_demand-covered")} value={`${coveredPct}%`} color={coveredPct < 98 ? lbb.brick : lbb.success} />
					<PhoneMetric
						label={t("roster-phone_overtime")}
						value={overtimeHours > 0 ? fmtHours(overtimeHours) : t("roster-phone_none")}
						color={overtimeHours > 0 ? lbb.brick : lbb.success}
					/>
				</div>

				<div style={{ padding: "10px 12px", borderBottom: lbb.borderHair, fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink60 }}>
					{t("roster-phone_people", { count: peopleCount })}
					<span style={{ color: freeCount > 0 ? lbb.success : lbb.ink60, fontWeight: 700, marginLeft: 6 }}>
						· {t("roster-phone_free", { count: freeCount })}
					</span>
				</div>

				<div style={{ padding: "4px 12px 12px" }}>
					{lines.length === 0 ? (
						<p style={{ margin: "16px 0", fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink60 }}>{t("roster-phone_empty")}</p>
					) : (
						lines.map((row) => (
							<section key={row.workcenter} style={{ marginTop: 14 }}>
								<div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
									<h3 style={{ margin: 0, fontFamily: lbb.fontBrand, fontSize: 17, color: lbb.navy }}>{row.workcenter}</h3>
									<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>{t("roster-phone_steps", { count: row.steps.length })}</span>
								</div>
								<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
									{row.steps.map((step) => (
										<StepCard key={step.workOrderId} step={step} />
									))}
								</div>
							</section>
						))
					)}
				</div>

				<p style={{ margin: 0, padding: "12px", borderTop: lbb.borderHair, fontFamily: lbb.fontUi, fontSize: 12, color: lbb.ink60 }}>
					{t("roster-phone_desktop-note")}
				</p>
			</div>
		</div>
	);
};

export default PhoneRoster;
